import type { FunctionComponent } from "react"

import classNames from "classnames"
import dynamic from "next/dynamic"

import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { AlgoliaLocationFilter } from "@/modules/jobAds/client/components/AlgoliaLocationFilter/AlgoliaLocationFilter"
import { AutocompleteSearchInput } from "@/modules/jobAds/client/components/AutocompleteSearchInput/AutocompleteSearchInput"
import { ContractFilter } from "@/modules/jobAds/client/components/ContractFilter/ContractFilter"
import { JOB_ADS_FO_WOOSMAP_MIGRATION } from "@/modules/jobAds/client/constants/flags"
import { isAbroadOptionEnabledForCurrentUser } from "@/modules/jobAds/client/utils/isAbroadOptionEnabledForCurrentUser"
import { useLocale } from "@/modules/locales/useLocale"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./PrimaryFilters.module.css"

type PrimaryFiltersProps = {
  className?: string
  forceHidingAbroadOption?: boolean
}

const DynamicGooglePlacesProvider = dynamic(() =>
  import("@/modules/google/components/GooglePlacesProvider").then(mod => mod.GooglePlacesProvider)
)

export const PrimaryFilters: FunctionComponent<PrimaryFiltersProps> = ({
  className,
  forceHidingAbroadOption = false,
}) => {
  const isJobAdsFoWoosmapMigrationEnabled = useFlag(JOB_ADS_FO_WOOSMAP_MIGRATION)
  const { t } = useTranslation(["job_ads_fo", "google_places"])
  const locale = useLocale()
  const { dataCurrentUser } = useCurrentUserFetch()
  const enableAbroadOption = !forceHidingAbroadOption && isAbroadOptionEnabledForCurrentUser(dataCurrentUser)

  return (
    <div className={classNames(styles.main, className)}>
      <AutocompleteSearchInput
        className={classNames(styles.primaryFilter, styles.searchInput)}
        inputContainerClassName={styles.searchInputContainer}
        clearLabel={t("job_ads_fo.client.search.keyword_search_input.clear_button_label")}
        locale={locale}
        placeholder={t("job_ads_fo.client.search.keyword_search_input.placeholder")}
        suggestionsTitle={t("job_ads_fo.client.search.keyword_search_input.suggestions_title")}
      />
      <ContractFilter
        applyLabel={t("job_ads_fo.client.search.contract_filter.applyLabel")}
        className={classNames(styles.primaryFilter, styles.contractFilter)}
        placeholder={t("job_ads_fo.client.search.contract_filter.placeholder")}
      />
      {isJobAdsFoWoosmapMigrationEnabled ? (
        <AlgoliaLocationFilter
          abroadOnlyLabel={t("job_ads_fo.client.search.algolia_location_filter.abroad_only")}
          className={styles.primaryFilter}
          clearLabel={t("job_ads_fo.client.search.location_filter.clearLabel")}
          enableAbroadOption={enableAbroadOption}
          placeholder={t("job_ads_fo.client.search.algolia_location_filter.placeholder")}
          radiusLabel={t("job_ads_fo.client.search.algolia_radius_filter.label")}
        />
      ) : (
        <DynamicGooglePlacesProvider
          googleApiNotReadyErrorText={t("google_places.client.error.google_api_not_ready")}
          googlePlacesApiKey={process.env.NEXT_PUBLIC_GOOGLE_FRONTEND_JOB_AD_FO_API_KEY ?? ""}
          missingApiKeyErrorText={t("google_places.client.error.missing_api_key")}
        >
          <AlgoliaLocationFilter
            abroadOnlyLabel={t("job_ads_fo.client.search.algolia_location_filter.abroad_only")}
            className={styles.primaryFilter}
            clearLabel={t("job_ads_fo.client.search.location_filter.clearLabel")}
            enableAbroadOption={enableAbroadOption}
            placeholder={t("job_ads_fo.client.search.algolia_location_filter.placeholder")}
            radiusLabel={t("job_ads_fo.client.search.algolia_radius_filter.label")}
          />
        </DynamicGooglePlacesProvider>
      )}
    </div>
  )
}
